import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useFirebaseSync from '../hooks/useFirebaseSync';
import SortableHeader from './SortableHeader';
import Pagination from './Pagination';
import OrderStatus from './OrderStatus';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const OrdersListStandalone = ({ 
  filters = { status: '', dateRange: 'all' },
  search = { term: '', field: 'all' }
}) => {
  const navigate = useNavigate();
  const [showCancelled, setShowCancelled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [sort, setSort] = useState({ field: 'createdAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Référence pour éviter les rendus inutiles
  const ordersRef = useRef(null);

  // Utiliser le hook useFirebaseSync pour les commandes
  const { 
    data: orders, 
    loading, 
    error, 
    syncWithShopify,
    lastSyncTime 
  } = useFirebaseSync('orders', {
    orderByField: 'createdAt',
    orderDirection: 'desc',
    limit: 100,
    autoSync: true, // Activer la synchronisation automatique
    where: [
      {
        field: 'status',
        operator: '!=',
        value: 'archived'
      }
    ]
  });

  // État pour le message de synchronisation
  const [syncMessage, setSyncMessage] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const syncTimeoutRef = useRef(null);
  const prevOrdersLengthRef = useRef(0);

  // Fonction pour afficher une notification
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Fonction pour synchroniser avec Shopify
  const handleSyncWithShopify = useCallback(async () => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    setSyncMessage('Synchronisation en cours...');
    
    try {
      const result = await syncWithShopify(true);
      
      if (result.success) {
        const message = result.ordersCount > 0 
          ? `Synchronisation réussie ! ${result.ordersCount} nouvelle(s) commande(s) trouvée(s).`
          : 'Synchronisation réussie ! Aucune nouvelle commande.';
        
        setSyncMessage(message);
        showNotification(message, 'success');
        toast.success(message);
        
        // Déclencher un rafraîchissement des données
        setRefreshTrigger(prev => prev + 1);
      } else if (result.skipped) {
        if (result.tooFrequent) {
          const message = 'Synchronisation ignorée (trop fréquente)';
          setSyncMessage(message);
          showNotification(message, 'info');
        } else {
          setSyncMessage('Synchronisation déjà en cours');
        }
      } else if (result.error) {
        const errorMessage = `Erreur: ${result.error}`;
        setSyncMessage(errorMessage);
        showNotification(errorMessage, 'error');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      const errorMessage = error?.message || 'Erreur inconnue lors de la synchronisation';
      setSyncMessage(`Erreur: ${errorMessage}`);
      showNotification(`Erreur: ${errorMessage}`, 'error');
      toast.error(`Erreur: ${errorMessage}`);
    } finally {
      // Réinitialiser l'état de synchronisation après un délai
      syncTimeoutRef.current = setTimeout(() => {
        setIsSyncing(false);
        // Garder le message de résultat visible
      }, 2000);
      
      setIsSyncing(false);
    }
  }, [syncWithShopify, isSyncing, showNotification, setRefreshTrigger]);

  // Nettoyer le timeout lors du démontage
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Mettre à jour le compteur de commandes précédent
  useEffect(() => {
    if (orders && orders.length > 0) {
      // Seulement mettre à jour si le nombre de commandes a changé
      if (prevOrdersLengthRef.current !== orders.length) {
        prevOrdersLengthRef.current = orders.length;
      }
    }
  }, [orders]);

  // Fonction pour créer une commande test
  const handleCreateTestOrder = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/mock/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors de la création de la commande test');
      
      const data = await response.json();
      if (data.success) {
        showNotification('Commande test créée avec succès', 'success');
        toast.success('Commande test créée avec succès');
      } else {
        throw new Error(data.error || 'Erreur lors de la création de la commande test');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification(error.message, 'error');
      toast.error(error.message);
    }
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return 'Date inconnue';
    
    try {
      // Si c'est un objet Timestamp de Firestore
      if (typeof date === 'object' && date.seconds) {
        const jsDate = new Date(date.seconds * 1000);
        return new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(jsDate);
      }
      
      // Si c'est déjà une date JavaScript ou une chaîne de caractères
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date));
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error, date);
      return 'Date invalide';
    }
  }, []);

  const formatAmount = useCallback((amount, currency = 'DZD') => {
    try {
      // Si le montant est une chaîne, remplacer les points par des virgules sauf le dernier
      let processedAmount = amount;
      if (typeof amount === 'string') {
        const parts = amount.split('.');
        if (parts.length > 2) {
          // Garder seulement le premier nombre et les deux décimales du dernier nombre
          processedAmount = parts[0] + '.' + parts[parts.length - 1].slice(0, 2);
        }
      }

      // Convertir en nombre
      const num = Number(processedAmount);
      if (isNaN(num)) {
        console.log('Montant invalide:', amount);
        return '0,00 DA';
      }

      // Formater avec le système français (virgule comme séparateur décimal)
      return num.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).replace('.', ',') + ' DA';
    } catch (error) {
      console.error('Erreur de formatage:', error);
      return '0,00 DA';
    }
  }, []);

  const handleSendMessage = useCallback((order) => {
    // Rediriger vers la page de messagerie avec le numéro prérempli
    navigate('/messaging', { state: { phoneNumber: order.customerPhone } });
  }, [navigate]);

  const handleUpdateOrder = useCallback(async (orderId, status) => {
    try {
      // Déterminer si c'est une commande test (ID < 10000) ou une vraie commande Shopify
      const isTestOrder = orderId < 10000;
      const endpoint = isTestOrder ? 
        `${API_URL}/api/mock/simulate/order/update/${orderId}` : 
        `${API_URL}/api/orders/update/${orderId}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      
      if (data.success) {
        showNotification(`Commande #${orderId} mise à jour avec succès`, 'success');
      } else {
        throw new Error(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification(error.message, 'error');
    }
  }, [showNotification]);

  const handleCancelOrder = useCallback(async (orderId) => {
    try {
      // Déterminer si c'est une commande test (ID < 10000) ou une vraie commande Shopify
      const isTestOrder = orderId < 10000;
      const endpoint = isTestOrder ? 
        `${API_URL}/api/mock/simulate/order/cancel/${orderId}` : 
        `${API_URL}/api/orders/cancel/${orderId}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        showNotification(`Commande #${orderId} annulée avec succès`, 'success');
      } else {
        throw new Error(data.error || 'Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification(error.message, 'error');
    }
  }, [showNotification]);

  // Trier les commandes
  const sortOrders = useCallback((orders) => {
    if (!sort.direction) return orders;

    return [...orders].sort((a, b) => {
      let aValue, bValue;

      switch (sort.field) {
        case 'orderNumber':
          aValue = a.orderNumber;
          bValue = b.orderNumber;
          break;
        case 'createdAt':
          aValue = a.createdAt?.seconds ? a.createdAt.seconds : new Date(a.createdAt).getTime() / 1000;
          bValue = b.createdAt?.seconds ? b.createdAt.seconds : new Date(b.createdAt).getTime() / 1000;
          break;
        case 'totalPrice':
          aValue = a.totalPrice || 0;
          bValue = b.totalPrice || 0;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (sort.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [sort]);

  // Filtrer et trier les commandes
  const filteredOrders = useMemo(() => {
    // Filtrer par statut
    let filtered = [...orders];
    if (!showCancelled) {
      filtered = filtered.filter(order => !order.cancelled);
    }

    // Filtrer par date
    if (filters.dateRange !== 'all') {
      const startDate = new Date(filters.dateRange.startDate);
      const endDate = new Date(filters.dateRange.endDate);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    // Filtrer par statut
    if (filters.status) {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Recherche textuelle si un terme de recherche est spécifié
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(order => {
        const orderNumber = order.orderNumber ? String(order.orderNumber).toLowerCase() : '';
        const customerName = order.customer?.name ? order.customer.name.toLowerCase() : '';
        const customerEmail = order.customer?.email ? order.customer.email.toLowerCase() : '';
        
        return (
          orderNumber.includes(searchLower) ||
          customerName.includes(searchLower) ||
          customerEmail.includes(searchLower)
        );
      });
    }

    return sortOrders(filtered);
  }, [orders, filters, search, showCancelled, sort, refreshTrigger]);

  // Obtenir les commandes de la page courante
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredOrders.slice(startIndex, startIndex + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  // Gérer le changement de page
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Gérer le changement de taille de page
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Retourner à la première page
  }, []);

  // Afficher un message d'erreur si nécessaire
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
        <div className="text-red-500 text-xl mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 inline-block mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Erreur de chargement des commandes
        </div>
        <div className="text-gray-700 mb-4">
          {error.message || "Une erreur s'est produite lors du chargement des commandes."}
        </div>
        <div className="text-sm text-gray-500 mb-4 p-4 bg-gray-100 rounded overflow-auto max-w-full">
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </div>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Rafraîchir la page
        </button>
      </div>
    );
  }

  // Afficher un indicateur de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-700">Chargement des commandes...</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {notification && (
        <div className={`p-4 mb-4 text-sm rounded-lg ${
          notification.type === 'success' ? 'bg-green-100 text-green-700' :
          notification.type === 'error' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="px-4 py-5 sm:p-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showCancelledCheckbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="h-4 w-4 text-indigo-600 rounded border-gray-300"
            />
            <label htmlFor="showCancelledCheckbox" className="ml-2 text-sm text-gray-600">
              Afficher les commandes annulées
            </label>
          </div>
          
          <div className="flex gap-2 flex-wrap items-center">
            <button
              onClick={handleSyncWithShopify}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              disabled={isSyncing}
            >
              {isSyncing ? 'Synchronisation...' : 'Synchroniser les commandes'}
            </button>
            
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={handleCreateTestOrder}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded"
              >
                Créer une commande test
              </button>
            )}
            
            {lastSyncTime && (
              <div className="text-sm text-gray-500">
                Dernière synchronisation : {formatDate(lastSyncTime)}
              </div>
            )}
            {syncMessage && (
              <div className="text-sm text-gray-500">
                {syncMessage}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <SortableHeader
                  label="Commande"
                  field="orderNumber"
                  currentSort={sort}
                  onSort={setSort}
                />
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <SortableHeader
                  label="Montant"
                  field="totalPrice"
                  currentSort={sort}
                  onSort={setSort}
                />
                <SortableHeader
                  label="Statut"
                  field="status"
                  currentSort={sort}
                  onSort={setSort}
                />
                <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOrders.map((order) => (
                <tr key={order.id} className={order.status === 'cancelled' ? 'bg-gray-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    #{order.orderNumber}
                    <div className="text-xs text-gray-500">{formatDate(order.createdAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.customerPhone}</div>
                    <div className="text-sm text-gray-500">{order.customerEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatAmount(order.totalPrice, order.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <OrderStatus status={order.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="space-x-2">
                      {order.customerPhone && order.status !== 'cancelled' && (
                        <>
                          <button
                            onClick={() => handleSendMessage(order)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Envoyer un message
                          </button>
                          <select
                            onChange={(e) => handleUpdateOrder(order.id, e.target.value)}
                            className="text-sm rounded border-gray-300 mr-2"
                            defaultValue=""
                          >
                            <option value="" disabled>Changer le statut</option>
                            <option value="pending">En attente</option>
                            <option value="confirmed">Confirmée</option>
                            <option value="delivered">Livrée</option>
                          </select>
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Annuler
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={filteredOrders.length}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default OrdersListStandalone;
